import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import PageLayout from "./PageLayout";
import { Account } from "./TopMenuBar";
import { useViewport } from "../hooks/useViewport";
import { ANDROID_TABLET_MENU_OFFSET, ANDROID_TABLET_SCROLL_REDUCTION } from "../lib/androidLayout";

const API_BASE = (import.meta as any).env?.VITE_API_BASE || "http://localhost:8100";

// Helper to get correct asset path for both web and Capacitor
function getAssetPath(path: string): string {
  const cleanPath = path.replace(/^\//, "");
  const isBrowser = typeof window !== "undefined";

  const cap = isBrowser ? (window as any).Capacitor : null;
  const isNative =
    Boolean(cap) &&
    (typeof cap.isNativePlatform === "function"
      ? cap.isNativePlatform()
      : typeof cap.getPlatform === "function"
        ? cap.getPlatform() === "ios" || cap.getPlatform() === "android"
        : cap.platform === "ios" || cap.platform === "android");

  if (isNative) {
    try {
      const baseUrl = new URL(window.location.href);
      return baseUrl.origin + "/" + cleanPath;
    } catch (e) {
      console.warn("[getAssetPath] Failed to construct URL, using fallback:", e);
      return "/" + cleanPath;
    }
  }

  return "./" + cleanPath;
}

// Helper to use api() function with JWT support (regular users and guests)
async function api<T>(path: string, opts: RequestInit = {}): Promise<T> {
  // Check for regular user JWT token first (all platforms - JWT is primary)
  let regularUserToken: string | null = null;
  if (typeof window !== 'undefined') {
    regularUserToken = localStorage.getItem('regular_user_jwt');
  }
  
  // Check for guest token as fallback
  const { getValidGuestToken } = await import('../lib/guest-auth');
  let guestToken = await getValidGuestToken();
  
  const headers: HeadersInit = { "Content-Type": "application/json", ...(opts.headers || {}) };
  // Prefer regular user token over guest token
  if (regularUserToken) {
    headers["Authorization"] = `Bearer ${regularUserToken}`;
    if ((import.meta as any).env?.DEV) {
      console.log(`[Leaderboard API] Sending regular user JWT token for: ${path}`);
    }
  } else if (guestToken) {
    headers["Authorization"] = `Bearer ${guestToken}`;
    if ((import.meta as any).env?.DEV) {
      console.log(`[Leaderboard API] Sending guest token for: ${path}`);
    }
  } else {
    if ((import.meta as any).env?.DEV) {
      console.warn(`[Leaderboard API] No token available for: ${path}`);
    }
  }

  const res = await fetch(`${API_BASE}${path}`, {
    credentials: "include",
    headers,
    ...opts,
  });

  if (!res.ok) {
    const text = await res.text();
    console.error(`[Leaderboard API] Request failed for ${path}: ${res.status} - ${text}`);
    throw new Error(text || `Request failed: ${res.status}`);
  }

  return res.json();
}

interface LeaderboardRow {
  rank: number;
  username: string;
  wins: number;
  losses: number;
  draws: number;
  glint_won: number;
  current_level: number;
  highest_level: number;
  player_type?: string;
}

interface LeaderboardResponse {
  rows: LeaderboardRow[];
  total: number;
  limit: number;
  offset: number;
  metric: string;
  period: string;
  mode: string;
}

interface MyRankResponse {
  rank: number | null;
  row: {
    username: string;
    wins: number;
    losses: number;
    draws: number;
    glint_won: number;
    current_level: number;
    highest_level: number;
  } | null;
}

interface TournamentLeaderboardRow {
  rank: number;
  username: string;
  events: number;
  first_place: number;
  second_place: number;
  podium_rate: number;
  best_tier: string | null;
  player_type?: string;
}

interface TournamentLeaderboardResponse {
  rows: TournamentLeaderboardRow[];
  total: number;
  limit: number;
  offset: number;
  period: string;
  tournament_type: string;
}

interface TournamentMyRankResponse {
  rank: number | null;
  row: {
    username: string;
    events: number;
    first_place: number;
    second_place: number;
    podium_rate: number;
    best_tier: string | null;
  } | null;
}

interface LeaderboardProps {
  account: Account;
  onLogout?: () => void;
}

const METRICS = ["wins", "glint", "highest_level", "current_level", "tournaments"] as const;
const PERIODS = ["all", "7d", "30d"] as const;
const MODES = ["all", "pve", "pvp"] as const;
const TOURNAMENT_TYPES = ["all", "daily", "weekly", "monthly", "high_roller", "season_finale"] as const;

const METRIC_LABELS: Record<string, string> = {
  wins: "Wins",
  glint: "Glint Won",
  highest_level: "Highest Level",
  current_level: "Current Level",
  tournaments: "Tourney",
};

const PERIOD_LABELS: Record<string, string> = {
  all: "All-time",
  "7d": "Last 7 days",
  "30d": "Last 30 days",
};

const MODE_LABELS: Record<string, string> = {
  all: "All",
  pve: "vs AI",
  pvp: "vs Human",
};

const TOURNAMENT_TYPE_LABELS: Record<string, string> = {
  all: "All Types",
  daily: "Daily",
  weekly: "Weekly",
  monthly: "Monthly",
  high_roller: "High Roller",
  season_finale: "Season Finale",
};

function formatGlint(n: number): string {
  return n.toFixed(2);
}

export default function Leaderboard({ account, onLogout }: LeaderboardProps) {
  // Debug: Log account info (DEV-only)
  if ((import.meta as any).env?.DEV) {
    console.log('[Leaderboard] Account info:', {
      id: account?.id,
      username: account?.username,
      email_verified: account?.email_verified,
      email: account?.email
    });
  }
  
  const [searchParams, setSearchParams] = useSearchParams();
  const isAndroid = typeof window !== 'undefined' && (window as any).Capacitor && (window as any).Capacitor.getPlatform() === 'android';
  const isIOSApp = typeof window !== 'undefined' && (window as any).Capacitor && (window as any).Capacitor.getPlatform && (window as any).Capacitor.getPlatform() === 'ios';
  const isIOSLarge = isIOSApp && typeof window !== 'undefined' ? window.innerWidth >= 768 : false;
  
  const metric = searchParams.get("metric") || "wins";
  const period = searchParams.get("period") || "all";
  // Mode is always "pvp" (vs Human) for tournaments, otherwise use the param or default to "all"
  const mode_param = searchParams.get("mode");
  const mode = metric === "tournaments" ? "pvp" : (mode_param || "all");
  // Tournament type is only relevant when metric is "tournaments"
  const tournament_type_param = searchParams.get("tournament_type");
  const tournament_type = metric === "tournaments" ? (tournament_type_param || "all") : null;
  const page = parseInt(searchParams.get("page") || "1", 10);
  
  const [leaderboard, setLeaderboard] = useState<LeaderboardResponse | null>(null);
  const [myRank, setMyRank] = useState<MyRankResponse | null>(null);
  const [tournamentLeaderboard, setTournamentLeaderboard] = useState<TournamentLeaderboardResponse | null>(null);
  const [tournamentMyRank, setTournamentMyRank] = useState<TournamentMyRankResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'metric' | 'period' | 'mode' | 'tournament_type' | null>(null);
  const filtersContainerRef = useRef<HTMLDivElement | null>(null);
  const filtersPanelRef = useRef<HTMLDivElement | null>(null);
  const rankCardRef = useRef<HTMLDivElement | null>(null);
  const tableScrollRef = useRef<HTMLDivElement | null>(null);
  const [filtersHeight, setFiltersHeight] = useState(0);
  const [rankCardHeight, setRankCardHeight] = useState(0);
  const [showTopIndicator, setShowTopIndicator] = useState(false);
  const [showBottomIndicator, setShowBottomIndicator] = useState(false);
  
  // Determine if we're showing tournament leaderboard
  const isTournamentLeaderboard = metric === "tournaments";
  const viewport = useViewport({ mobileBreakpoint: 1024 });
  const isPortrait = viewport.isPortrait && viewport.width < 1024;
  const isMobileLandscape = !viewport.isPortrait && viewport.isMobile;
  
  // Bulletproof iPhone detection - exclude iPad (must be after viewport declaration)
  const isIPhone = (() => {
    if (!isIOSApp) return false;
    const userAgent = typeof navigator !== 'undefined' ? (navigator.userAgent || navigator.vendor || '').toLowerCase() : '';
    const platform = typeof navigator !== 'undefined' ? (navigator as any).platform || '' : '';
    const maxTouchPoints = typeof navigator !== 'undefined' ? (navigator as any).maxTouchPoints || 0 : 0;
    const maxDim = Math.max(viewport.width, viewport.height);
    const minDim = Math.min(viewport.width, viewport.height);
    
    // Explicit iPad in user agent
    if (userAgent.includes('ipad')) return false;
    
    // Modern iPadOS 13+ reports as MacIntel with touch support
    if (platform.toLowerCase() === 'macintel' && maxTouchPoints > 1 && maxDim >= 768 && minDim >= 768) return false;
    
    // If dimensions suggest iPad, exclude it
    if (maxDim >= 768 && minDim >= 768) return false;
    
    // Explicit iPhone in user agent (but not iPad)
    if (userAgent.includes('iphone') && !userAgent.includes('ipad')) return true;
    
    // iPod touch counts as iPhone-like
    if (userAgent.includes('ipod')) return true;
    
    // If it's iOS app and not iPad (by dimensions), assume iPhone
    if (isIOSApp && (maxDim < 768 || minDim < 768)) return true;
    
    return false;
  })();
  
  // Bulletproof iPad detection - exclude iPhone (must be after isIPhone declaration)
  const isIOSPad = (() => {
    if (!isIOSApp) return false;
    if (isIPhone) return false; // If it's iPhone, it's not iPad
    return Math.max(viewport.width, viewport.height) >= 768;
  })();
  const isAndroidTablet = viewport.isAndroidTablet;
  
  // iPhone should use Android phone layout for content (not menu or background) (must be after isAndroidTablet declaration)
  const useAndroidPhoneLayout = (isAndroid && !isAndroidTablet) || isIPhone;
  const androidTableInsetLeft = isAndroid ? Math.round(viewport.width * (70 / 1920)) : 0;
  const androidTableInsetRight = isAndroid ? Math.round(viewport.width * (112 / 1920)) + 1 : 0;
  const androidMenuOffset = isAndroidTablet ? ANDROID_TABLET_MENU_OFFSET : 0;
  const backgroundSrc = (isIOSPad || isIPhone) ? "TabletBackground.png" : "LeaderboardBackground.png";
  const iosPadGutterX = isIOSPad ? Math.round(viewport.width * 0.05) : 0;
  const iosPadGutterY = isIOSPad ? Math.round(viewport.height * 0.05) : 0;
  const iosPadShiftX = isIOSPad ? 10 : 0;
  const iosPadShiftY = isIOSPad ? 24 : 0;
  const iosTopPad = isIOSPad ? 72 : 0;
  const iosBottomPad = isIOSPad ? 115 : 0;
  // iPhone-specific adjustments: move right and make narrower
  const iphoneRightShift = isIPhone ? 10 : 0; // Move right by 10px (keeps left side in place)
  const iphoneRightNarrow = isIPhone ? 27 : 0; // Add 27px to right to make narrower (increased by 2px)
  const mobileTopOffset = isMobileLandscape
    ? (isAndroidTablet
        ? `calc(32px + ${androidMenuOffset}px)`
        : (useAndroidPhoneLayout
            ? '32px'
            : (isIOSPad
                ? `calc(env(safe-area-inset-top, 0px) + ${iosTopPad}px + ${iosPadGutterY / 2 + iosPadShiftY}px)`
                : 'env(safe-area-inset-top, 0px)')))
    : (isIOSPad
        ? `calc(env(safe-area-inset-top, 0px) + ${iosTopPad}px)`
        : 'env(safe-area-inset-top, 0px)');
  const DEFAULT_FILTER_HEIGHT = 44;
  const DEFAULT_RANK_HEIGHT = 78;
  const FILTER_TO_RANK_GAP = 0;
  const RANK_TO_TABLE_GAP = 0;
  const hasRankCard = account.email_verified && (isTournamentLeaderboard ? !!(tournamentMyRank && tournamentMyRank.row) : !!(myRank && myRank.row));
  // Debug logging (DEV-only)
  if (account.email_verified && (import.meta as any).env?.DEV) {
    console.log('[Leaderboard] Rank card check:', {
      email_verified: account.email_verified,
      isTournamentLeaderboard,
      tournamentMyRank: tournamentMyRank ? { hasRow: !!tournamentMyRank.row, rank: tournamentMyRank.rank } : null,
      myRank: myRank ? { hasRow: !!myRank.row, rank: myRank.rank } : null,
      hasRankCard
    });
  }
  const effectiveFiltersHeight = isMobileLandscape ? (filtersHeight || DEFAULT_FILTER_HEIGHT) : 0;
  const effectiveRankHeight = hasRankCard ? (rankCardHeight || DEFAULT_RANK_HEIGHT) : 0;
  const mobileRankTop = isMobileLandscape
    ? `calc(${mobileTopOffset} + ${effectiveFiltersHeight}px + ${FILTER_TO_RANK_GAP}px)`
    : undefined;
  const mobileTableTop = isMobileLandscape
    ? hasRankCard
      ? `calc(${mobileTopOffset} + ${effectiveFiltersHeight}px + ${FILTER_TO_RANK_GAP}px + ${effectiveRankHeight}px + ${RANK_TO_TABLE_GAP}px)`
      : `calc(${mobileTopOffset} + ${effectiveFiltersHeight}px + ${RANK_TO_TABLE_GAP}px)`
    : undefined;
  const tableBottomOffset = isAndroidTablet ? ANDROID_TABLET_SCROLL_REDUCTION : 0;
  const textScale = isAndroidTablet ? 3 : (isIOSPad ? 1.1 : 1);
  const androidContentShift = isAndroidTablet ? 25 : 0;
  const androidFilterFontSize = isAndroidTablet ? '18px' : undefined; // filter tab/option text
  const androidTableFontSize = isAndroidTablet ? '22px' : undefined; // table text size (keep body at the size you liked)
  const androidHeaderFontSize = isAndroidTablet ? '22px' : undefined; // slight bump for headers (dialed back)
  const androidLineHeight = isAndroidTablet ? 1.25 : undefined;
  const androidButtonFontSize = androidFilterFontSize;
  // iOS iPad font sizes
  const iosPadFilterFontSize = isIOSPad ? '18px' : undefined; // filter tab/option text
  const iosPadTableFontSize = isIOSPad ? '18px' : undefined; // table text size
  const iosPadHeaderFontSize = isIOSPad ? '18px' : undefined; // table headers
  const iosPadLineHeight = isIOSPad ? 1.5 : undefined; // increased for better spacing
  const iosPadButtonFontSize = iosPadFilterFontSize;
  // iOS iPad spacing
  const iosPadFilterPadding = isIOSPad ? '0.75rem 1rem' : undefined; // filter button padding
  const iosPadFilterGap = isIOSPad ? '0.5rem' : undefined; // gap between filter buttons
  const iosPadTableCellPadding = isIOSPad ? '0.75rem 1.25rem' : undefined; // table cell padding
  const iosPadTableHeaderPadding = isIOSPad ? '0.25rem 1.25rem 0.5rem 1.25rem' : undefined; // table header padding (reduced top, keep bottom)
  const iosPadRankCardPadding = isIOSPad ? '1rem 1.5rem' : undefined; // rank card padding
  const iosPadFilterPanelPadding = isIOSPad ? '1rem' : undefined; // filter panel padding
  const iosPadFilterPanelGap = isIOSPad ? '0.75rem' : undefined; // gap in filter panel buttons
  
  const limit = 25;
  const offset = (page - 1) * limit;

  const updateScrollIndicators = (el: HTMLDivElement | null) => {
    if (!el) return;
    const isAtTop = el.scrollTop === 0;
    const isAtBottom = el.scrollHeight - el.scrollTop <= el.clientHeight + 1;
    setShowTopIndicator(!isAtTop);
    setShowBottomIndicator(!isAtBottom);
  };

  useEffect(() => {
    if (isIOSPad && tableScrollRef.current) {
      requestAnimationFrame(() => updateScrollIndicators(tableScrollRef.current));
    }
  }, [isIOSPad, leaderboard, tournamentLeaderboard, isMobileLandscape]);

  useLayoutEffect(() => {
    if (!isMobileLandscape || typeof window === "undefined") {
      setFiltersHeight(0);
      setRankCardHeight(0);
      return;
    }

    const measure = () => {
      const container = filtersContainerRef.current;
      const panel = filtersPanelRef.current;
      let totalFiltersHeight = 0;

      if (container) {
        const containerStyles = window.getComputedStyle(container);
        const paddingY = parseFloat(containerStyles.paddingTop || "0") + parseFloat(containerStyles.paddingBottom || "0");
        totalFiltersHeight = Math.max(container.offsetHeight - paddingY, 0);
        if (panel) {
          const panelStyles = window.getComputedStyle(panel);
          const panelMargins = parseFloat(panelStyles.marginTop || "0") + parseFloat(panelStyles.marginBottom || "0");
          totalFiltersHeight = Math.max(totalFiltersHeight - (panel.offsetHeight + panelMargins), 0);
        }
      }

      setFiltersHeight(totalFiltersHeight);

      const rankEl = rankCardRef.current;
      if (rankEl) {
        const rankStyles = window.getComputedStyle(rankEl);
        const rankMargins = parseFloat(rankStyles.marginTop || "0") + parseFloat(rankStyles.marginBottom || "0");
        const nextRankHeight = rankEl.offsetHeight + rankMargins;
        setRankCardHeight(nextRankHeight);
      } else {
        setRankCardHeight(0);
      }
    };

    measure();

    const ResizeObserverImpl = (window as any).ResizeObserver;
    if (!ResizeObserverImpl) {
      return;
    }

    const observer = new ResizeObserverImpl(measure);
    if (filtersContainerRef.current) observer.observe(filtersContainerRef.current);
    if (filtersPanelRef.current) observer.observe(filtersPanelRef.current);
    if (rankCardRef.current) observer.observe(rankCardRef.current);

    return () => observer.disconnect();
  }, [
    isMobileLandscape,
    activeTab,
    isTournamentLeaderboard,
    account.email_verified,
    metric,
    period,
    mode,
    tournament_type,
    myRank?.row,
    tournamentMyRank?.row,
  ]);
  
  useEffect(() => {
    const fetchLeaderboard = async () => {
      setLoading(true);
      setError(null);
      
      try {
        if (isTournamentLeaderboard) {
          // Fetch tournament leaderboard
          const params = new URLSearchParams({
            period,
            tournament_type: tournament_type || "all",
            limit: limit.toString(),
            offset: offset.toString(),
          });
          
          const tournamentData = await api<TournamentLeaderboardResponse>(`/tournament-leaderboard?${params}`);
          setTournamentLeaderboard(tournamentData);
          setLeaderboard(null); // Clear regular leaderboard
          
          // Only fetch my-rank for verified users
          if (account.email_verified) {
            try {
              const myRankParams = new URLSearchParams({
                period,
                tournament_type: tournament_type || "all",
              });
              if ((import.meta as any).env?.DEV) {
                console.log('[Leaderboard] Fetching tournament my-rank with params:', myRankParams.toString());
              }
              const myRankData = await api<TournamentMyRankResponse>(`/tournament-leaderboard/my-rank?${myRankParams}`);
              if ((import.meta as any).env?.DEV) {
                console.log('[Leaderboard] Tournament my-rank data received:', myRankData);
              }
              setTournamentMyRank(myRankData);
            } catch (err) {
              console.error('[Leaderboard] Failed to fetch tournament my-rank:', err);
              setTournamentMyRank(null);
            }
          } else {
            if ((import.meta as any).env?.DEV) {
              console.log('[Leaderboard] Skipping tournament my-rank fetch - email not verified');
            }
            setTournamentMyRank(null);
          }
        } else {
          // Fetch regular leaderboard
          const params = new URLSearchParams({
            metric,
            period,
            mode,
            limit: limit.toString(),
            offset: offset.toString(),
          });
          
          const leaderboardData = await api<LeaderboardResponse>(`/leaderboard?${params}`);
          setLeaderboard(leaderboardData);
          setTournamentLeaderboard(null); // Clear tournament leaderboard
          
          // Only fetch my-rank for verified users
          if (account.email_verified) {
            try {
              const myRankParams = new URLSearchParams({
                metric,
                period,
                mode,
              });
              if ((import.meta as any).env?.DEV) {
                console.log('[Leaderboard] Fetching my-rank with params:', myRankParams.toString());
              }
              const myRankData = await api<MyRankResponse>(`/leaderboard/my-rank?${myRankParams}`);
              if ((import.meta as any).env?.DEV) {
                console.log('[Leaderboard] My-rank data received:', myRankData);
              }
              setMyRank(myRankData);
            } catch (err) {
              console.error('[Leaderboard] Failed to fetch my-rank:', err);
              setMyRank(null);
            }
          } else {
            if ((import.meta as any).env?.DEV) {
              console.log('[Leaderboard] Skipping my-rank fetch - email not verified');
            }
            setMyRank(null);
          }
        }
        
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to load leaderboard");
        console.error("Leaderboard fetch error:", err);
      } finally {
        setLoading(false);
      }
    };
    
    fetchLeaderboard();
  }, [metric, period, mode, tournament_type, page, account.email_verified, isTournamentLeaderboard, offset]);
  
  const updateParam = (key: string, value: string) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set(key, value);
    if (key !== "page") {
      newParams.set("page", "1"); // Reset to first page when changing filters
    }
    // When switching to tournaments metric, automatically set tournament_type to "all" and mode to "pvp"
    if (key === "metric" && value === "tournaments") {
      newParams.set("tournament_type", "all");
      newParams.set("mode", "pvp");
    }
    // When switching away from tournaments metric, remove tournament_type param
    if (key === "metric" && value !== "tournaments") {
      newParams.delete("tournament_type");
      // Reset mode to "all" if it was "pvp" (from tournaments)
      if (newParams.get("mode") === "pvp") {
        newParams.set("mode", "all");
      }
    }
    setSearchParams(newParams);
    // Close the active tab after selecting a filter
    setActiveTab(null);
  };
  
  const handleTabClick = (tab: 'metric' | 'period' | 'mode' | 'tournament_type') => {
    // Toggle: if clicking the same tab, close it; otherwise open the new one
    setActiveTab(activeTab === tab ? null : tab);
  };
  
  const totalPages = isTournamentLeaderboard 
    ? (tournamentLeaderboard ? Math.ceil(tournamentLeaderboard.total / limit) : 1)
    : (leaderboard ? Math.ceil(leaderboard.total / limit) : 1);
  
  const filtersStyle = isMobileLandscape
    ? (isIOSPad ? {
      top: mobileTopOffset,
      left: `calc(env(safe-area-inset-left, 0px) + 32px + 112px + ${iosPadGutterX}px)`,
      right: `calc(env(safe-area-inset-right, 0px) + 48px + ${iosPadGutterX}px)`,
      marginLeft: '-20px',
      marginRight: 0,
      transform: (androidContentShift || iosPadShiftX) ? `translateX(${(androidContentShift || 0) + iosPadShiftX}px)` : undefined,
      fontSize: iosPadFilterFontSize || androidFilterFontSize,
      lineHeight: iosPadLineHeight || androidLineHeight,
    } : {
      top: mobileTopOffset,
      left: useAndroidPhoneLayout
        ? `calc(env(safe-area-inset-left, 0px) + 32px + 112px + ${androidTableInsetLeft}px + ${iphoneRightShift}px)`
        : 'calc(env(safe-area-inset-left, 0px) + 32px + 112px)',
      right: useAndroidPhoneLayout
        ? `calc(env(safe-area-inset-right, 0px) + 48px + ${androidTableInsetRight}px + ${iphoneRightNarrow}px)`
        : 'calc(env(safe-area-inset-right, 0px) + 48px)',
      marginLeft: '-20px',
      marginRight: 0,
      transform: androidContentShift ? `translateX(${androidContentShift}px)` : undefined,
      fontSize: androidFilterFontSize,
      lineHeight: androidLineHeight,
    })
    : (useAndroidPhoneLayout ? { paddingLeft: `${androidTableInsetLeft}px`, paddingRight: `${androidTableInsetRight}px` } : {});

  return (
    <PageLayout account={account} onLogout={onLogout}>
      {/* Native full-frame background image */}
      {(isAndroid || isIOSPad || isIPhone) && (
        <div
          className="fixed inset-0 w-screen h-screen overflow-hidden"
          style={{ zIndex: 0, pointerEvents: "none" }}
          aria-hidden="true"
        >
          <img
            src={getAssetPath(backgroundSrc)}
            alt=""
            className="absolute inset-0 w-full h-full"
            style={{ 
              objectFit: "cover", 
              objectPosition: "center"
            }}
            draggable={false}
          />
        </div>
      )}
      <div style={isAndroid ? { position: "relative", zIndex: 1, fontSize: textScale > 1 ? `${textScale}em` : undefined, lineHeight: textScale > 1 ? 1.15 : undefined } : (textScale > 1 ? { fontSize: `${textScale}em`, lineHeight: 1.15 } : undefined)}>
      
      {/* Filters section - show for all users */}
      <div 
        ref={filtersContainerRef}
        className={isMobileLandscape ? "fixed z-40 p-2" : "px-2 md:px-6 pb-4"}
        style={filtersStyle}
      >
        {/* Tab Headers - Above the bar */}
        <div
          className="flex rounded-xl border-2 mb-3 flex-wrap"
          style={{
            borderColor: '#8b6542',
            background: '#fffaeb',
            rowGap: isIOSPad ? '20px' : '6px',
            columnGap: iosPadFilterGap || '0.5rem',
            gap: isIOSPad ? undefined : '0.5rem',
          }}
        >
            {/* Metric Tab - always show */}
            <button
              onClick={() => handleTabClick('metric')}
              className={`flex-1 px-2 py-2 transition-all border-r-2 cursor-pointer flex items-center justify-center gap-1 ${isMobileLandscape ? 'text-xs' : ''}`}
              style={{
                borderColor: '#8b6542',
                color: '#3e2615',
                borderTopLeftRadius: '0.75rem',
                borderBottomLeftRadius: '0.75rem',
                background: activeTab === 'metric' ? '#f7b267' : '#fffaeb',
                fontSize: iosPadButtonFontSize || androidButtonFontSize,
                padding: iosPadFilterPadding,
                gap: iosPadFilterGap,
                minWidth: 0,
                flexBasis: 0,
              }}
            >
              <svg 
                width={isMobileLandscape ? "12" : "16"} 
                height={isMobileLandscape ? "12" : "16"} 
                viewBox="0 0 16 16" 
                className="flex-shrink-0"
                style={{ 
                  fill: '#3e2615',
                }}
              >
                {activeTab === 'metric' ? (
                  <path d="M8 11L3 6h10z" fill="#3e2615" />
                ) : (
                  <path d="M6 3l5 5-5 5V3z" fill="#3e2615" />
                )}
              </svg>
              <div className={`${isMobileLandscape ? 'text-xs' : 'text-sm md:text-base'} font-semibold text-center flex-1 min-w-0`} style={{ color: '#3e2615', fontSize: iosPadButtonFontSize || androidButtonFontSize, wordWrap: 'break-word', overflowWrap: 'break-word', lineHeight: '1.3' }}>
                <span className="underline">Metric</span>: {METRIC_LABELS[metric]}
              </div>
            </button>
            
            {/* Period Tab */}
            <button
              onClick={() => handleTabClick('period')}
              className={`flex-1 px-2 py-2 transition-all border-r-2 cursor-pointer flex items-center justify-center gap-1 ${isMobileLandscape ? 'text-xs' : ''}`}
              style={{
                borderColor: '#8b6542',
                color: '#3e2615',
                background: activeTab === 'period' ? '#f7b267' : '#fffaeb',
                fontSize: iosPadButtonFontSize || androidButtonFontSize,
                padding: iosPadFilterPadding,
                gap: iosPadFilterGap,
                minWidth: 0,
                flexBasis: 0,
              }}
            >
              <svg 
                width={isMobileLandscape ? "12" : "16"} 
                height={isMobileLandscape ? "12" : "16"} 
                viewBox="0 0 16 16" 
                className="flex-shrink-0"
                style={{ 
                  fill: '#3e2615',
                }}
              >
                {activeTab === 'period' ? (
                  <path d="M8 11L3 6h10z" fill="#3e2615" />
                ) : (
                  <path d="M6 3l5 5-5 5V3z" fill="#3e2615" />
                )}
              </svg>
              <div className={`${isMobileLandscape ? 'text-xs' : 'text-sm md:text-base'} font-semibold text-center flex-1 min-w-0`} style={{ color: '#3e2615', fontSize: iosPadButtonFontSize || androidButtonFontSize, wordWrap: 'break-word', overflowWrap: 'break-word', lineHeight: '1.3' }}>
                <span className="underline">Period</span>: {PERIOD_LABELS[period]}
              </div>
            </button>
            
            {/* Tournament Type Tab - always show, but disabled when metric is not tournaments */}
            <button
              onClick={() => isTournamentLeaderboard && handleTabClick('tournament_type')}
              disabled={!isTournamentLeaderboard}
              className={`flex-1 px-2 py-2 transition-all flex items-center justify-center gap-1 ${isMobileLandscape ? 'text-xs' : ''} ${!isTournamentLeaderboard ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'} ${isTournamentLeaderboard ? '' : 'border-r-2'}`}
              style={{
                borderColor: '#8b6542',
                color: '#3e2615',
                background: activeTab === 'tournament_type' ? '#f7b267' : '#fffaeb',
                fontSize: iosPadButtonFontSize || androidButtonFontSize,
                padding: iosPadFilterPadding,
                gap: iosPadFilterGap,
                minWidth: 0,
                flexBasis: 0,
                ...(isTournamentLeaderboard ? {
                  borderTopRightRadius: '0.75rem',
                  borderBottomRightRadius: '0.75rem',
                } : {}),
              }}
            >
              <svg 
                width={isMobileLandscape ? "12" : "16"} 
                height={isMobileLandscape ? "12" : "16"} 
                viewBox="0 0 16 16" 
                className="flex-shrink-0"
                style={{ 
                  fill: '#3e2615',
                }}
              >
                {activeTab === 'tournament_type' ? (
                  <path d="M8 11L3 6h10z" fill="#3e2615" />
                ) : (
                  <path d="M6 3l5 5-5 5V3z" fill="#3e2615" />
                )}
              </svg>
              <div className={`${isMobileLandscape ? 'text-xs' : 'text-sm md:text-base'} font-semibold text-center flex-1 min-w-0`} style={{ color: '#3e2615', fontSize: iosPadButtonFontSize || androidButtonFontSize, wordWrap: 'break-word', overflowWrap: 'break-word', lineHeight: '1.3' }}>
                <span className="underline">Tourney</span>: {isTournamentLeaderboard ? TOURNAMENT_TYPE_LABELS[tournament_type || "all"] : "n/a"}
              </div>
            </button>
            
            {/* Mode Tab - always show, but disabled when in tournament leaderboard mode */}
            <button
              onClick={() => !isTournamentLeaderboard && handleTabClick('mode')}
              disabled={isTournamentLeaderboard}
              className={`flex-1 px-2 py-2 transition-all flex items-center justify-center gap-1 ${isMobileLandscape ? 'text-xs' : ''} ${isTournamentLeaderboard ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}
              style={{
                borderColor: '#8b6542',
                color: '#3e2615',
                borderTopRightRadius: '0.75rem',
                borderBottomRightRadius: '0.75rem',
                background: activeTab === 'mode' ? '#f7b267' : '#fffaeb',
                fontSize: iosPadButtonFontSize || androidButtonFontSize,
                padding: iosPadFilterPadding,
                gap: iosPadFilterGap,
                minWidth: 0,
                flexBasis: 0,
              }}
            >
              <svg 
                width={isMobileLandscape ? "12" : "16"} 
                height={isMobileLandscape ? "12" : "16"} 
                viewBox="0 0 16 16" 
                className="flex-shrink-0"
                style={{ 
                  fill: '#3e2615',
                }}
              >
                {activeTab === 'mode' ? (
                  <path d="M8 11L3 6h10z" fill="#3e2615" />
                ) : (
                  <path d="M6 3l5 5-5 5V3z" fill="#3e2615" />
                )}
              </svg>
              <div className={`${isMobileLandscape ? 'text-xs' : 'text-sm md:text-base'} font-semibold text-center flex-1 min-w-0`} style={{ color: '#3e2615', fontSize: iosPadButtonFontSize || androidButtonFontSize, wordWrap: 'break-word', overflowWrap: 'break-word', lineHeight: '1.3' }}>
                <span className="underline">Mode</span>: {MODE_LABELS[mode]}
              </div>
            </button>
          </div>
                              
          {/* Filter Options Panel - Above the bar */}
          {activeTab && (
            <div
              ref={filtersPanelRef}
              className={`mb-3 rounded-xl border-2 ${isMobileLandscape ? 'p-2' : 'p-4'}`}
              style={{
                background: '#fffaeb',
                borderColor: '#8b6542',
                padding: iosPadFilterPanelPadding,
              }}
            >
              {activeTab === 'metric' && (
                <div className="flex flex-wrap gap-2" style={{ gap: iosPadFilterPanelGap }}>
                  {METRICS.map((m) => (
                    <button
                      key={m}
                      onClick={() => updateParam("metric", m)}
                      className={`${isMobileLandscape ? 'px-2 py-1 text-xs' : 'px-4 py-2 text-sm'} rounded-lg font-semibold transition-all`}
                      style={{
                        border: `2px solid ${metric === m ? '#8b6542' : '#c4a07a'}`,
                        background: metric === m ? '#f7b267' : '#fffaeb',
                        color: '#3e2615',
                        fontSize: iosPadButtonFontSize || androidButtonFontSize,
                        padding: iosPadFilterPadding,
                      }}
                    >
                      {METRIC_LABELS[m]}
                    </button>
                  ))}
                </div>
              )}
              
              {activeTab === 'period' && (
                <div className="flex flex-wrap gap-2" style={{ gap: iosPadFilterPanelGap }}>
                  {PERIODS.map((p) => (
                    <button
                      key={p}
                      onClick={() => updateParam("period", p)}
                      className={`${isMobileLandscape ? 'px-2 py-1 text-xs' : 'px-4 py-2 text-sm'} rounded-lg font-semibold transition-all`}
                      style={{
                        border: `2px solid ${period === p ? '#8b6542' : '#c4a07a'}`,
                        background: period === p ? '#f7b267' : '#fffaeb',
                        color: '#3e2615',
                        fontSize: iosPadButtonFontSize || androidButtonFontSize,
                        padding: iosPadFilterPadding,
                      }}
                    >
                      {PERIOD_LABELS[p]}
                    </button>
                  ))}
                </div>
              )}
              
              {activeTab === 'mode' && !isTournamentLeaderboard && (
                <div className="flex flex-wrap gap-2" style={{ gap: iosPadFilterPanelGap }}>
                  {MODES.map((m) => (
                    <button
                      key={m}
                      onClick={() => updateParam("mode", m)}
                      className={`${isMobileLandscape ? 'px-2 py-1 text-xs' : 'px-4 py-2 text-sm'} rounded-lg font-semibold transition-all`}
                      style={{
                        border: `2px solid ${mode === m ? '#8b6542' : '#c4a07a'}`,
                        background: mode === m ? '#f7b267' : '#fffaeb',
                        color: '#3e2615',
                        fontSize: iosPadButtonFontSize || androidButtonFontSize,
                        padding: iosPadFilterPadding,
                      }}
                    >
                      {MODE_LABELS[m]}
                    </button>
                  ))}
                </div>
              )}

              {activeTab === 'tournament_type' && isTournamentLeaderboard && (
                <div className="flex flex-wrap gap-2" style={{ gap: iosPadFilterPanelGap }}>
                  {TOURNAMENT_TYPES.map((t) => (
                    <button
                      key={t}
                      onClick={() => updateParam("tournament_type", t)}
                      className={`${isMobileLandscape ? 'px-2 py-1 text-xs' : 'px-4 py-2 text-sm'} rounded-lg font-semibold transition-all`}
                      style={{
                        border: `2px solid ${(tournament_type || "all") === t ? '#8b6542' : '#c4a07a'}`,
                        background: (tournament_type || "all") === t ? '#f7b267' : '#fffaeb',
                        color: '#3e2615',
                        fontSize: iosPadButtonFontSize || androidButtonFontSize,
                        padding: iosPadFilterPadding,
                      }}
                    >
                      {TOURNAMENT_TYPE_LABELS[t]}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
      </div>
          
      {/* Your Rank Card - only show for verified users - separate from filters */}
      {account.email_verified && (isTournamentLeaderboard ? (
        tournamentMyRank && tournamentMyRank.row ? (
          <div
            ref={rankCardRef}
            className={isMobileLandscape ? "fixed z-35 p-2" : "px-2 md:px-6 pb-4"}
            style={isMobileLandscape ? {
              top: mobileRankTop,
              left: isAndroid
                ? `calc(env(safe-area-inset-left, 0px) + 32px + 112px + ${androidTableInsetLeft}px)`
                : `calc(env(safe-area-inset-left, 0px) + 32px + 112px + ${isIOSPad ? iosPadGutterX : 0}px)`,
              right: isAndroid
                ? `calc(env(safe-area-inset-right, 0px) + 48px + ${androidTableInsetRight}px)`
                : `calc(env(safe-area-inset-right, 0px) + 48px + ${isIOSPad ? iosPadGutterX : 0}px)`,
              marginLeft: '-20px',
              marginRight: 0,
              transform: (androidContentShift || iosPadShiftX) ? `translateX(${(androidContentShift || 0) + iosPadShiftX}px)` : undefined,
              fontSize: iosPadFilterFontSize || androidFilterFontSize,
              lineHeight: iosPadLineHeight || androidLineHeight,
            } : (useAndroidPhoneLayout ? { paddingLeft: `${androidTableInsetLeft}px`, paddingRight: `${androidTableInsetRight}px` } : {})}
          >
            <div
              className="rounded-xl border-2"
              style={{
                background: 'linear-gradient(to bottom, #f7b267 0%, #f4a261 100%)',
                borderColor: '#8b6542',
                padding: iosPadRankCardPadding || (isMobileLandscape ? '0.125rem 0.375rem' : '0.5rem 1rem'),
              }}
            >
              {isPortrait ? (
                <div className={`flex flex-col ${isMobileLandscape ? 'gap-0.5' : 'gap-1.5'}`} style={{ gap: isIOSPad ? '1rem' : undefined }}>
                  <div className="flex justify-center">
                    <div className={`font-bold ${isMobileLandscape ? 'text-sm' : 'text-xl'}`} style={{ color: '#3e2615', fontSize: iosPadFilterFontSize || (androidFilterFontSize ? '21px' : androidFilterFontSize), lineHeight: iosPadLineHeight || androidLineHeight }}>
                      {tournamentMyRank.rank !== null 
                        ? `Your Rank: #${tournamentMyRank.rank} in ${TOURNAMENT_TYPE_LABELS[tournament_type]} Tournaments (${PERIOD_LABELS[period]})`
                        : "Unranked"}
                    </div>
              </div>
              <div className="flex items-center gap-3" style={{ fontSize: iosPadFilterFontSize || androidFilterFontSize, lineHeight: iosPadLineHeight || androidLineHeight, gap: isIOSPad ? '1.5rem' : undefined }}>
                <div className={`flex flex-col ${isMobileLandscape ? 'gap-0' : 'gap-0.5'} flex-1`} style={{ color: '#6b4423', gap: isIOSPad ? '0.75rem' : undefined }}>
                  <div className={`${isMobileLandscape ? 'text-xs' : 'text-base'} font-medium border-b border-[#8b6542] border-opacity-30 ${isMobileLandscape ? 'pb-0' : 'pb-0.5'}`} style={{ paddingBottom: isIOSPad ? '0.5rem' : undefined }}>
                    {tournamentMyRank.row.events} events
                  </div>
                  <div className={`${isMobileLandscape ? 'text-xs' : 'text-base'} border-b border-[#8b6542] border-opacity-30 ${isMobileLandscape ? 'pb-0' : 'pb-0.5'}`} style={{ paddingBottom: isIOSPad ? '0.5rem' : undefined }}>
                    🥇 {tournamentMyRank.row.first_place} firsts • 🥈 {tournamentMyRank.row.second_place} seconds
                  </div>
                  <div className={isMobileLandscape ? 'text-xs' : 'text-base'}>
                    Podium Rate: {tournamentMyRank.row.podium_rate.toFixed(1)}%
                    {tournamentMyRank.row.best_tier && ` • Best Tier: ${TOURNAMENT_TYPE_LABELS[tournamentMyRank.row.best_tier]}`}
                  </div>
                    </div>
                    <div className="flex-shrink-0">
                      <Link
                        to="/play"
                        className="px-5 py-3 rounded-lg text-base font-semibold transition-all hover:scale-105 whitespace-nowrap inline-block"
                        style={{
                          background: '#fffaeb',
                          color: '#3e2615',
                          border: '2px solid #8b6542',
                          textDecoration: 'none',
                        }}
                      >
                        To the Mine
                      </Link>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-between gap-3" style={{ gap: isIOSPad ? '1.5rem' : undefined }}>
                  <div className={`flex flex-col ${isMobileLandscape ? 'gap-0' : 'gap-0.5'}`} style={{ gap: isIOSPad ? '0.75rem' : undefined }}>
                    <div className={`font-bold ${isMobileLandscape ? 'text-sm' : 'text-lg'}`} style={{ color: '#3e2615', fontSize: iosPadFilterFontSize || (androidFilterFontSize ? '21px' : androidFilterFontSize), lineHeight: iosPadLineHeight || androidLineHeight }}>
                      {tournamentMyRank.rank !== null 
                        ? `Your Rank: #${tournamentMyRank.rank} in ${TOURNAMENT_TYPE_LABELS[tournament_type]} Tournaments (${PERIOD_LABELS[period]})`
                        : "Unranked"}
                    </div>
                    <div className={`${isMobileLandscape ? 'text-xs' : 'text-sm'} flex flex-wrap gap-2`} style={{ color: '#6b4423', fontSize: iosPadFilterFontSize || androidFilterFontSize, lineHeight: iosPadLineHeight || androidLineHeight, gap: isIOSPad ? '0.75rem' : undefined }}>
                      <span>{tournamentMyRank.row.events} events</span>
                      <span>•</span>
                      <span>🥇 {tournamentMyRank.row.first_place} firsts</span>
                      <span>•</span>
                      <span>🥈 {tournamentMyRank.row.second_place} seconds</span>
                      <span>•</span>
                      <span>Podium Rate: {tournamentMyRank.row.podium_rate.toFixed(1)}%</span>
                      {tournamentMyRank.row.best_tier && (
                        <>
                          <span>•</span>
                          <span>Best Tier: {TOURNAMENT_TYPE_LABELS[tournamentMyRank.row.best_tier]}</span>
                        </>
                      )}
                    </div>
                  </div>
                  <Link
                    to="/play"
                    className="px-4 py-2 rounded-lg text-sm font-semibold transition-all hover:scale-105 whitespace-nowrap flex-shrink-0"
                    style={{
                      background: '#fffaeb',
                      color: '#3e2615',
                      border: '2px solid #8b6542',
                      textDecoration: 'none',
                    }}
                  >
                    To the Mine
                  </Link>
                </div>
              )}
            </div>
          </div>
        ) : null
        ) : (
          myRank && myRank.row ? (
            <div
              ref={rankCardRef}
              className={isMobileLandscape ? "fixed z-35 p-2" : "px-2 md:px-6 pb-4"}
              style={isMobileLandscape ? {
                top: mobileRankTop,
                left: useAndroidPhoneLayout
                  ? `calc(env(safe-area-inset-left, 0px) + 32px + 112px + ${androidTableInsetLeft}px + ${iphoneRightShift}px)`
                  : `calc(env(safe-area-inset-left, 0px) + 32px + 112px + ${isIOSPad ? iosPadGutterX : 0}px)`,
                right: useAndroidPhoneLayout
                  ? `calc(env(safe-area-inset-right, 0px) + 48px + ${androidTableInsetRight}px + ${iphoneRightNarrow}px)`
                  : `calc(env(safe-area-inset-right, 0px) + 48px + ${isIOSPad ? iosPadGutterX : 0}px)`,
                marginLeft: '-20px',
                marginRight: 0,
                transform: (androidContentShift || iosPadShiftX) ? `translateX(${(androidContentShift || 0) + iosPadShiftX}px)` : undefined,
                fontSize: iosPadFilterFontSize || androidFilterFontSize,
                lineHeight: iosPadLineHeight || androidLineHeight,
              } : (useAndroidPhoneLayout ? { paddingLeft: `${androidTableInsetLeft}px`, paddingRight: `${androidTableInsetRight}px` } : {})}
            >
              <div
                className="rounded-xl border-2"
                style={{
                  background: 'linear-gradient(to bottom, #f7b267 0%, #f4a261 100%)',
                  borderColor: '#8b6542',
                  padding: isMobileLandscape ? '0.125rem 0.375rem' : '0.5rem 1rem',
                }}
              >
            {isPortrait ? (
              // Portrait: Centered title, win record above glint/level on left, button on right (vertically centered)
              <div className={`flex flex-col ${isMobileLandscape ? 'gap-0.5' : 'gap-1.5'}`} style={{ gap: isIOSPad ? '1rem' : undefined }}>
                {/* Title centered */}
                  <div className="flex justify-center">
                    <div className={`font-bold ${isMobileLandscape ? 'text-sm' : 'text-xl'}`} style={{ color: '#3e2615', fontSize: iosPadFilterFontSize || undefined }}>
                      {myRank.rank !== null ? `Your Rank: #${myRank.rank}` : "Unranked"}
                    </div>
                  </div>
                
                {/* Two columns: left (win record + glint/level), right (button) */}
                <div className="flex items-center gap-3" style={{ gap: isIOSPad ? '1.5rem' : undefined }}>
                  {/* Win record and glint/level on left with underlines */}
                  <div className={`flex flex-col ${isMobileLandscape ? 'gap-0' : 'gap-0.5'} flex-1`} style={{ color: '#6b4423', gap: isIOSPad ? '0.75rem' : undefined }}>
                    <div className={`${isMobileLandscape ? 'text-xs' : 'text-base'} font-medium border-b border-[#8b6542] border-opacity-30 ${isMobileLandscape ? 'pb-0' : 'pb-0.5'}`} style={{ paddingBottom: isIOSPad ? '0.5rem' : undefined }}>
                      {myRank.row.wins}W / {myRank.row.losses}L / {myRank.row.draws}D
                    </div>
                    <div className={`${isMobileLandscape ? 'text-xs' : 'text-base'} border-b border-[#8b6542] border-opacity-30 ${isMobileLandscape ? 'pb-0' : 'pb-0.5'}`} style={{ paddingBottom: isIOSPad ? '0.5rem' : undefined }}>
                      ✧{formatGlint(myRank.row.glint_won)} Glint Won
                    </div>
                    <div className={isMobileLandscape ? 'text-xs' : 'text-base'}>
                      Level {myRank.row.current_level} (Highest: {myRank.row.highest_level})
                    </div>
                  </div>
                  
                  {/* Button on right - vertically centered */}
                  <div className="flex-shrink-0">

                    <Link
                      to="/play"
                      className="px-5 py-3 rounded-lg text-base font-semibold transition-all hover:scale-105 whitespace-nowrap inline-block"
                      style={{
                        background: '#fffaeb',
                        color: '#3e2615',
                        border: '2px solid #8b6542',
                        textDecoration: 'none',
                      }}
                    >
                      To the Mine
                    </Link>
                  </div>
                </div>
              </div>
            ) : (
              // Landscape/Desktop: Title on top, stats below with button on right
              <div className="flex items-center justify-between gap-3" style={{ gap: isIOSPad ? '1.5rem' : undefined }}>
                {/* Title and stats on left */}
                <div className={`flex flex-col ${isMobileLandscape ? 'gap-0' : 'gap-0.5'}`} style={{ gap: isIOSPad ? '0.75rem' : undefined }}>
                  <div className={`font-bold ${isMobileLandscape ? 'text-sm' : 'text-lg'}`} style={{ color: '#3e2615', fontSize: iosPadFilterFontSize || (androidFilterFontSize ? '21px' : androidFilterFontSize), lineHeight: iosPadLineHeight || androidLineHeight }}>
                    {myRank.rank !== null ? `Your Rank: #${myRank.rank}` : "Unranked"}
                  </div>
                  <div className={`${isMobileLandscape ? 'text-xs' : 'text-sm'} flex flex-wrap gap-2`} style={{ color: '#6b4423', fontSize: iosPadFilterFontSize || androidFilterFontSize, lineHeight: iosPadLineHeight || androidLineHeight, gap: isIOSPad ? '0.75rem' : undefined }}>
                    <span>{myRank.row.wins}W / {myRank.row.losses}L / {myRank.row.draws}D</span>
                    <span>•</span>
                    <span>✧{formatGlint(myRank.row.glint_won)} Glint Won</span>
                    <span>•</span>
                    <span>Level {myRank.row.current_level} (Highest: {myRank.row.highest_level})</span>
                  </div>
                </div>
                
                {/* Button on right - bigger */}
                <Link
                  to="/play"
                  className="px-4 py-2 rounded-lg text-sm font-semibold transition-all hover:scale-105 whitespace-nowrap flex-shrink-0"
                  style={{
                    background: '#fffaeb',
                    color: '#3e2615',
                    border: '2px solid #8b6542',
                    textDecoration: 'none',
                  }}
                >
                  To the Mine
                </Link>
              </div>
            )}
            </div>
          </div>
          ) : null
        )
      )}
      
      {/* Leaderboard table */}
      <div 
        className={isMobileLandscape ? "fixed z-30 p-2" : "px-2 md:px-6 pb-6"}
        style={isMobileLandscape ? (
          isIOSPad ? {
            top: mobileTableTop,
            left: `calc(env(safe-area-inset-left, 0px) + 32px + 112px + ${iosPadGutterX}px)`,
            right: `calc(env(safe-area-inset-right, 0px) + 48px + ${iosPadGutterX}px)`,
            bottom: tableBottomOffset
              ? `calc(env(safe-area-inset-bottom, 8px) - 14px + 46px + ${tableBottomOffset}px + ${iosBottomPad}px)`
              : `calc(env(safe-area-inset-bottom, 8px) - 14px + 46px + ${iosBottomPad}px)`,
            display: 'flex',
            flexDirection: 'column',
            marginLeft: '-20px',
            marginRight: 0,
            transform: (androidContentShift || iosPadShiftX) ? `translateX(${(androidContentShift || 0) + iosPadShiftX}px)` : undefined,
            fontSize: iosPadTableFontSize || androidTableFontSize,
            lineHeight: iosPadLineHeight || androidLineHeight,
          } : {
            top: mobileTableTop,
            left: useAndroidPhoneLayout
              ? `calc(env(safe-area-inset-left, 0px) + 32px + 112px + ${androidTableInsetLeft}px + ${iphoneRightShift}px)`
              : 'calc(env(safe-area-inset-left, 0px) + 32px + 112px)',
            right: useAndroidPhoneLayout
              ? `calc(env(safe-area-inset-right, 0px) + 48px + ${androidTableInsetRight}px + ${iphoneRightNarrow}px)`
              : 'calc(env(safe-area-inset-right, 0px) + 48px)',
            bottom: tableBottomOffset
              ? `calc(env(safe-area-inset-bottom, 8px) - 14px + 56px + ${tableBottomOffset}px)`
              : 'calc(env(safe-area-inset-bottom, 8px) - 14px + 56px)', // Slightly higher clipping on Android/mobile web
            overflowY: 'auto',
            overflowX: 'auto',
            display: 'flex',
            flexDirection: 'column',
            marginLeft: '-20px',
            marginRight: 0,
            transform: androidContentShift ? `translateX(${androidContentShift}px)` : undefined,
            fontSize: androidTableFontSize,
            lineHeight: androidLineHeight,
          }
        ) : (useAndroidPhoneLayout ? { paddingLeft: `${androidTableInsetLeft}px`, paddingRight: `${androidTableInsetRight}px` } : {})}
      >
        <div className={isMobileLandscape && isIOSPad ? "relative flex-1 min-h-0" : ""} style={isMobileLandscape && isIOSPad ? { overflow: 'visible', display: 'flex', flexDirection: 'column' } : {}}>
          <div
            ref={isMobileLandscape && isIOSPad ? tableScrollRef : undefined}
            onScroll={isMobileLandscape && isIOSPad ? (e) => updateScrollIndicators(e.currentTarget as HTMLDivElement) : undefined}
            className={isMobileLandscape ? (isIOSPad ? "h-full overflow-y-auto overflow-x-auto pr-1" : "overflow-y-auto overflow-x-auto") : ""}
            style={isMobileLandscape ? (isIOSPad ? { paddingRight: '4px', flex: 1, minHeight: 0 } : { 
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
            }) : {}}
          >
        {loading ? (
          <div className="text-center py-8" style={{ color: '#3e2615' }}>Loading leaderboard...</div>
        ) : error ? (
          <div className="text-center py-8 text-red-600">{error}</div>
        ) : isTournamentLeaderboard ? (
          tournamentLeaderboard && tournamentLeaderboard.rows.length === 0 ? (
            <div className="text-center py-8" style={{ color: '#3e2615' }}>No players found for these filters.</div>
          ) : tournamentLeaderboard ? (
            <>
              <div
              className={`rounded-2xl border-4 overflow-x-auto ${isMobileLandscape ? 'p-2 flex-1' : 'p-6'}`}
              style={isMobileLandscape ? {
                background: '#fffaeb',
                borderColor: '#8b6542',
                boxShadow: '0 4px 6px rgba(0,0,0,0.1), 0 2px 4px rgba(0,0,0,0.06)',
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0,
                padding: isIOSPad ? '1.5rem' : undefined,
              } : {
                background: '#fffaeb',
                borderColor: '#8b6542',
                padding: isIOSPad ? '1.5rem' : undefined,
              }}
              >
                <table className="w-full" style={{ color: '#3e2615', fontSize: iosPadTableFontSize || androidTableFontSize, lineHeight: iosPadLineHeight || androidLineHeight }}>
                  <thead>
                    <tr className="border-b-2" style={{ borderColor: '#8b6542' }}>
                      <th className={`text-left ${isMobileLandscape ? 'py-1 px-1 text-xs' : 'py-2 px-4'} font-bold`} style={{ fontSize: iosPadHeaderFontSize || androidHeaderFontSize || iosPadTableFontSize || androidTableFontSize, lineHeight: iosPadLineHeight || androidLineHeight, padding: iosPadTableHeaderPadding, width: isIOSPad ? '60px' : undefined }}>Rank</th>
                      <th className={`text-left ${isMobileLandscape ? 'py-1 px-1 text-xs' : 'py-2 px-4'} font-bold`} style={{ fontSize: iosPadHeaderFontSize || androidHeaderFontSize || iosPadTableFontSize || androidTableFontSize, lineHeight: iosPadLineHeight || androidLineHeight, padding: iosPadTableHeaderPadding, width: isIOSPad ? '140px' : undefined }}>Username</th>
                      <th className={`text-right ${isMobileLandscape ? 'py-1 px-1 text-xs' : 'py-2 px-4'} font-bold`} style={{ fontSize: iosPadHeaderFontSize || androidHeaderFontSize || iosPadTableFontSize || androidTableFontSize, lineHeight: iosPadLineHeight || androidLineHeight, padding: iosPadTableHeaderPadding }}>Events</th>
                      <th className={`text-right ${isMobileLandscape ? 'py-1 px-1 text-xs' : 'py-2 px-4'} font-bold`} style={{ fontSize: iosPadHeaderFontSize || androidHeaderFontSize || iosPadTableFontSize || androidTableFontSize, lineHeight: iosPadLineHeight || androidLineHeight, padding: iosPadTableHeaderPadding }}>🥇 1st</th>
                      <th className={`text-right ${isMobileLandscape ? 'py-1 px-1 text-xs' : 'py-2 px-4'} font-bold`} style={{ fontSize: iosPadHeaderFontSize || androidHeaderFontSize || iosPadTableFontSize || androidTableFontSize, lineHeight: iosPadLineHeight || androidLineHeight, padding: iosPadTableHeaderPadding }}>🥈 2nd</th>
                      <th className={`text-right ${isMobileLandscape ? 'py-1 px-1 text-xs' : 'py-2 px-4'} font-bold`} style={{ fontSize: iosPadHeaderFontSize || androidHeaderFontSize || iosPadTableFontSize || androidTableFontSize, lineHeight: iosPadLineHeight || androidLineHeight, padding: iosPadTableHeaderPadding }}>Podium</th>
                      {tournament_type === "all" && (
                        <th className={`text-right ${isMobileLandscape ? 'py-1 px-1 text-xs' : 'py-2 px-4'} font-bold`} style={{ fontSize: iosPadHeaderFontSize || androidHeaderFontSize || iosPadTableFontSize || androidTableFontSize, lineHeight: iosPadLineHeight || androidLineHeight, padding: iosPadTableHeaderPadding }}>Best</th>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {tournamentLeaderboard.rows.map((row) => {
                      const isCurrentUser = row.username === account.username;
                      return (
                        <tr
                          key={row.rank}
                          className={`border-b ${isCurrentUser ? "font-bold" : ""}`}
                          style={{
                            borderColor: '#c4a07a',
                            background: isCurrentUser
                              ? "linear-gradient(to bottom, #f7b267 0%, #f4a261 100%)"
                              : "transparent",
                          }}
                        >
                          <td className={`${isMobileLandscape ? 'py-1 px-1 text-xs' : 'py-2 px-4'}`} style={{ fontSize: iosPadTableFontSize || androidTableFontSize, lineHeight: iosPadLineHeight || androidLineHeight, padding: iosPadTableCellPadding, width: isIOSPad ? '60px' : undefined }}>#{row.rank}</td>
                          <td className={`${isMobileLandscape ? 'py-1 px-1 text-xs' : 'py-2 px-4'}`} style={{ fontSize: iosPadTableFontSize || androidTableFontSize, lineHeight: iosPadLineHeight || androidLineHeight, padding: iosPadTableCellPadding, width: isIOSPad ? '140px' : undefined }}>
                            {row.username}
                            {(row.player_type === "llm" || ["Lump", "Ferris", "Magnus"].includes(row.username)) && " [AI]"}
                          </td>
                          <td className={`${isMobileLandscape ? 'py-1 px-1 text-xs' : 'py-2 px-4'} text-right`} style={{ fontSize: iosPadTableFontSize || androidTableFontSize, lineHeight: iosPadLineHeight || androidLineHeight, padding: iosPadTableCellPadding }}>{row.events}</td>
                          <td className={`${isMobileLandscape ? 'py-1 px-1 text-xs' : 'py-2 px-4'} text-right`} style={{ fontSize: iosPadTableFontSize || androidTableFontSize, lineHeight: iosPadLineHeight || androidLineHeight, padding: iosPadTableCellPadding }}>{row.first_place}</td>
                          <td className={`${isMobileLandscape ? 'py-1 px-1 text-xs' : 'py-2 px-4'} text-right`} style={{ fontSize: iosPadTableFontSize || androidTableFontSize, lineHeight: iosPadLineHeight || androidLineHeight, padding: iosPadTableCellPadding }}>{row.second_place}</td>
                          <td className={`${isMobileLandscape ? 'py-1 px-1 text-xs' : 'py-2 px-4'} text-right`} style={{ fontSize: iosPadTableFontSize || androidTableFontSize, lineHeight: iosPadLineHeight || androidLineHeight, padding: iosPadTableCellPadding }}>{row.podium_rate.toFixed(1)}%</td>
                          {tournament_type === "all" && (
                            <td className={`${isMobileLandscape ? 'py-1 px-1 text-xs' : 'py-2 px-4'} text-right`} style={{ fontSize: iosPadTableFontSize || androidTableFontSize, lineHeight: iosPadLineHeight || androidLineHeight, padding: iosPadTableCellPadding }}>
                              {row.best_tier ? TOURNAMENT_TYPE_LABELS[row.best_tier] : "-"}
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              
              {/* Pagination */}
              {totalPages > 1 && (
                <div className={`flex justify-center items-center gap-2 ${isMobileLandscape ? 'mt-2' : 'mt-4'}`}>
                  <button
                    onClick={() => updateParam("page", Math.max(1, page - 1).toString())}
                    disabled={page === 1}
                    className={`${isMobileLandscape ? 'px-2 py-1 text-xs' : 'px-4 py-2 text-sm'} rounded-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed`}
                    style={{
                      background: page === 1 ? '#c4a07a' : '#fffaeb',
                      color: '#3e2615',
                      border: '2px solid #8b6542',
                    }}
                  >
                    Prev
                  </button>
                  <span className={isMobileLandscape ? 'text-xs' : 'text-sm'} style={{ color: '#3e2615' }}>
                    {page}/{totalPages}
                  </span>
                  <button
                    onClick={() => updateParam("page", Math.min(totalPages, page + 1).toString())}
                    disabled={page === totalPages}
                    className={`${isMobileLandscape ? 'px-2 py-1 text-xs' : 'px-4 py-2 text-sm'} rounded-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed`}
                    style={{
                      background: page === totalPages ? '#c4a07a' : '#fffaeb',
                      color: '#3e2615',
                      border: '2px solid #8b6542',
                    }}
                  >
                    Next
                  </button>
                </div>
              )}
            </>
          ) : null
        ) : (

          leaderboard && leaderboard.rows.length === 0 ? (
            <div className="text-center py-8" style={{ color: '#3e2615' }}>No players found for these filters.</div>
          ) : leaderboard ? (
            <>
              <div
              className={`rounded-2xl border-4 overflow-x-auto ${isMobileLandscape ? 'p-2 flex-1' : 'p-6'}`}
              style={isMobileLandscape ? {
                background: '#fffaeb',
                borderColor: '#8b6542',
                boxShadow: '0 4px 6px rgba(0,0,0,0.1), 0 2px 4px rgba(0,0,0,0.06)',
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0,
                padding: isIOSPad ? '1.5rem' : undefined,
              } : {
                background: '#fffaeb',
                borderColor: '#8b6542',
                padding: isIOSPad ? '1.5rem' : undefined,
              }}
              >
                <table className="w-full" style={{ color: '#3e2615', fontSize: iosPadTableFontSize || androidTableFontSize, lineHeight: iosPadLineHeight || androidLineHeight }}>
                  <thead>
                    <tr className="border-b-2" style={{ borderColor: '#8b6542' }}>
                      <th className={`text-left ${isMobileLandscape ? 'py-1 px-1 text-xs' : 'py-2 px-4'} font-bold`} style={{ fontSize: iosPadHeaderFontSize || androidHeaderFontSize || iosPadTableFontSize || androidTableFontSize, lineHeight: iosPadLineHeight || androidLineHeight, padding: iosPadTableHeaderPadding, width: isIOSPad ? '60px' : undefined }}>Rank</th>
                      <th className={`text-left ${isMobileLandscape ? 'py-1 px-1 text-xs' : 'py-2 px-4'} font-bold`} style={{ fontSize: iosPadHeaderFontSize || androidHeaderFontSize || iosPadTableFontSize || androidTableFontSize, lineHeight: iosPadLineHeight || androidLineHeight, padding: iosPadTableHeaderPadding, width: isIOSPad ? '140px' : undefined }}>User</th>
                      <th className={`text-right ${isMobileLandscape ? 'py-1 px-1 text-xs' : 'py-2 px-4'} font-bold`} style={{ fontSize: iosPadHeaderFontSize || androidHeaderFontSize || iosPadTableFontSize || androidTableFontSize, lineHeight: iosPadLineHeight || androidLineHeight, padding: iosPadTableHeaderPadding }}>W</th>
                      <th className={`text-right ${isMobileLandscape ? 'py-1 px-1 text-xs' : 'py-2 px-4'} font-bold`} style={{ fontSize: iosPadHeaderFontSize || androidHeaderFontSize || iosPadTableFontSize || androidTableFontSize, lineHeight: iosPadLineHeight || androidLineHeight, padding: iosPadTableHeaderPadding }}>L</th>
                      <th className={`text-right ${isMobileLandscape ? 'py-1 px-1 text-xs' : 'py-2 px-4'} font-bold`} style={{ fontSize: iosPadHeaderFontSize || androidHeaderFontSize || iosPadTableFontSize || androidTableFontSize, lineHeight: iosPadLineHeight || androidLineHeight, padding: iosPadTableHeaderPadding }}>D</th>
                      <th className={`text-right ${isMobileLandscape ? 'py-1 px-1 text-xs' : 'py-2 px-4'} font-bold`} style={{ fontSize: iosPadHeaderFontSize || androidHeaderFontSize || iosPadTableFontSize || androidTableFontSize, lineHeight: iosPadLineHeight || androidLineHeight, padding: iosPadTableHeaderPadding }}>Glint</th>
                      {isIOSPad ? (
                        <th 
                          className={`text-right ${isMobileLandscape ? 'py-1 px-1 text-xs' : 'py-2 px-4'} font-bold ${!isIOSPad ? 'cursor-help' : ''}`}
                          title={!isIOSPad ? "Format: Current Level / Highest Level. Current Level = highest level you can play based on your Glint balance. Highest Level = highest level you've ever played." : undefined}
                          style={{ fontSize: iosPadHeaderFontSize || androidHeaderFontSize || iosPadTableFontSize || androidTableFontSize, lineHeight: iosPadLineHeight || androidLineHeight, padding: iosPadTableHeaderPadding }}
                        >
                          <div className="flex flex-col items-end" style={{ gap: '0.125rem', lineHeight: '1.2' }}>
                            <div style={{ fontSize: iosPadHeaderFontSize || androidHeaderFontSize || iosPadTableFontSize || androidTableFontSize }}>Level</div>
                            <div style={{ fontSize: '12px', fontWeight: 'normal', opacity: 0.75, lineHeight: '1', marginTop: '-2px' }}>(Curr / High)</div>
                          </div>
                        </th>
                      ) : (
                        <>
                          <th 
                            className={`text-right ${isMobileLandscape ? 'py-1 px-1 text-xs' : 'py-2 px-4'} font-bold cursor-help`}
                            title="The highest level you can currently play based on your Glint balance"
                            style={{ fontSize: androidHeaderFontSize, lineHeight: androidLineHeight }}
                          >
                            Current Lvl
                          </th>
                          <th 
                            className={`text-right ${isMobileLandscape ? 'py-1 px-1 text-xs' : 'py-2 px-4'} font-bold cursor-help`}
                            title="The highest level you've ever played"
                            style={{ fontSize: androidHeaderFontSize, lineHeight: androidLineHeight }}
                          >
                            High Lvl
                          </th>
                        </>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {leaderboard.rows.map((row) => {
                      const isCurrentUser = row.username === account.username;
                      return (
                        <tr
                          key={row.rank}
                          className={`border-b ${isCurrentUser ? "font-bold" : ""}`}
                          style={{
                            borderColor: '#c4a07a',
                            background: isCurrentUser
                              ? "linear-gradient(to bottom, #f7b267 0%, #f4a261 100%)"
                              : "transparent",
                          }}
                        >
                          <td className={`${isMobileLandscape ? 'py-1 px-1 text-xs' : 'py-2 px-4'}`} style={{ fontSize: iosPadTableFontSize || androidTableFontSize, lineHeight: iosPadLineHeight || androidLineHeight, padding: iosPadTableCellPadding, width: isIOSPad ? '60px' : undefined }}>#{row.rank}</td>
                          <td className={`${isMobileLandscape ? 'py-1 px-1 text-xs' : 'py-2 px-4'}`} style={{ fontSize: iosPadTableFontSize || androidTableFontSize, lineHeight: iosPadLineHeight || androidLineHeight, padding: iosPadTableCellPadding, width: isIOSPad ? '140px' : undefined }}>
                            {row.username}
                            {(row.player_type === "llm" || ["Lump", "Ferris", "Magnus"].includes(row.username)) && " [AI]"}
                          </td>
                          <td className={`${isMobileLandscape ? 'py-1 px-1 text-xs' : 'py-2 px-4'} text-right`} style={{ fontSize: iosPadTableFontSize || androidTableFontSize, lineHeight: iosPadLineHeight || androidLineHeight, padding: iosPadTableCellPadding }}>{row.wins}</td>
                          <td className={`${isMobileLandscape ? 'py-1 px-1 text-xs' : 'py-2 px-4'} text-right`} style={{ fontSize: iosPadTableFontSize || androidTableFontSize, lineHeight: iosPadLineHeight || androidLineHeight, padding: iosPadTableCellPadding }}>{row.losses}</td>
                          <td className={`${isMobileLandscape ? 'py-1 px-1 text-xs' : 'py-2 px-4'} text-right`} style={{ fontSize: iosPadTableFontSize || androidTableFontSize, lineHeight: iosPadLineHeight || androidLineHeight, padding: iosPadTableCellPadding }}>{row.draws}</td>
                          <td className={`${isMobileLandscape ? 'py-1 px-1 text-xs' : 'py-2 px-4'} text-right`} style={{ fontSize: iosPadTableFontSize || androidTableFontSize, lineHeight: iosPadLineHeight || androidLineHeight, padding: iosPadTableCellPadding }}>✧{formatGlint(row.glint_won)}</td>
                          {isIOSPad ? (
                            <td className={`${isMobileLandscape ? 'py-1 px-1 text-xs' : 'py-2 px-4'} text-right`} style={{ fontSize: iosPadTableFontSize || androidTableFontSize, lineHeight: iosPadLineHeight || androidLineHeight, padding: iosPadTableCellPadding }}>
                              {row.current_level} / {row.highest_level}
                            </td>
                          ) : (
                            <>
                              <td className={`${isMobileLandscape ? 'py-1 px-1 text-xs' : 'py-2 px-4'} text-right`} style={{ fontSize: androidTableFontSize, lineHeight: androidLineHeight }}>{row.current_level}</td>
                              <td className={`${isMobileLandscape ? 'py-1 px-1 text-xs' : 'py-2 px-4'} text-right`} style={{ fontSize: androidTableFontSize, lineHeight: androidLineHeight }}>{row.highest_level}</td>
                            </>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>            

            {/* Pagination */}
            {totalPages > 1 && (
              <div className={`flex justify-center items-center gap-2 ${isMobileLandscape ? 'mt-2' : 'mt-4'}`}>
                <button
                  onClick={() => updateParam("page", Math.max(1, page - 1).toString())}
                  disabled={page === 1}
                  className={`${isMobileLandscape ? 'px-2 py-1 text-xs' : 'px-4 py-2 text-sm'} rounded-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed`}
                  style={{
                    background: page === 1 ? '#c4a07a' : '#fffaeb',
                    color: '#3e2615',
                    border: '2px solid #8b6542',
                  }}
                >
                  Prev
                </button>
                <span className={isMobileLandscape ? 'text-xs' : 'text-sm'} style={{ color: '#3e2615' }}>
                  {page}/{totalPages}
                </span>
                <button
                  onClick={() => updateParam("page", Math.min(totalPages, page + 1).toString())}
                  disabled={page === totalPages}
                  className={`${isMobileLandscape ? 'px-2 py-1 text-xs' : 'px-4 py-2 text-sm'} rounded-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed`}
                  style={{
                    background: page === totalPages ? '#c4a07a' : '#fffaeb',
                    color: '#3e2615',
                    border: '2px solid #8b6542',
                  }}
                >
                  Next
                </button>
              </div>
            )}
          </>
        ) : null
        )}
          </div>
          {isIOSPad && isMobileLandscape && (
            <>
              {showTopIndicator && (
                <div className="pointer-events-none absolute left-1/2 -translate-x-1/2 flex justify-center" style={{ top: '0px', zIndex: 45 }}>
                  <svg width={28} height={18} viewBox="0 0 12 8" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ filter: 'drop-shadow(0 2px 2px rgba(0,0,0,0.65))' }}>
                    <path d="M1 7L6 2L11 7" stroke="#f7b267" strokeWidth="3.4" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
              )}
              {showBottomIndicator && (
                <div className="pointer-events-none absolute left-1/2 -translate-x-1/2 flex justify-center" style={{ bottom: '-20px', zIndex: 45 }}>
                  <svg width={28} height={18} viewBox="0 0 12 8" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ filter: 'drop-shadow(0 2px 2px rgba(0,0,0,0.65))' }}>
                    <path d="M1 1L6 6L11 1" stroke="#f7b267" strokeWidth="3.4" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
              )}
            </>
          )}
        </div>
      </div>
      </div>
    </PageLayout>
  );
}
